/**
 * Column schema and row mapping for the Quick Review field-diff table.
 * @module quickReviewDiffTable
 */
import { QuickReviewFieldComparison } from "../../../../data-model/quickReviewModel";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";
import { TableCellType, TableHeader } from "../../../custom-table/table";

/**
 * Monitored field names whose Parameter label differs from the capitalized
 * raw name. Keys are snake_case API field names or legacy preview labels;
 * values are the labels shown in the diff table.
 */
const QUICK_REVIEW_FIELD_LABELS: Record<string, string> = {
  instructions: "Appointment Notes",
  regimen_name: "Appointment Type",
  practitioner_name: "Provider",
  bo_value: "Estimate",
  estimate_value: "Estimate",
};

/** Column keys for the Quick Review field-diff table. */
export enum QuickReviewDiffTableColumnKeys {
  /** Human-readable monitored field name */
  PARAMETER = "parameter",
  /** Value the order was last worked in */
  OLD_VALUE = "oldValue",
  /** Newly ingested value from the board row */
  NEW_VALUE = "newValue",
  /** Whether worked and new values differ; drives row highlight styling */
  IS_CHANGED = "isChanged",
}

/** One row of the Quick Review field-diff table. */
export interface QuickReviewDiffTableData {
  [QuickReviewDiffTableColumnKeys.PARAMETER]: string;
  [QuickReviewDiffTableColumnKeys.OLD_VALUE]: string;
  [QuickReviewDiffTableColumnKeys.NEW_VALUE]: string;
  [QuickReviewDiffTableColumnKeys.IS_CHANGED]: boolean;
}

/** Column definitions and row mapping for the Quick Review modal field-diff table. */
export class QuickReviewDiffTableUtils {
  /**
   * Column headers for the Quick Review field-diff table: the monitored
   * field name plus the value the order was worked in and the newly
   * ingested value (both wrapping, never truncated).
   */
  static generateColumnsForQuickReviewDiffTable(): TableHeader[] {
    return [
      {
        label: "Parameter",
        key: QuickReviewDiffTableColumnKeys.PARAMETER,
        order: 1,
        width: 20,
        type: TableCellType.STRING,
      },
      {
        label: "Previous Value",
        key: QuickReviewDiffTableColumnKeys.OLD_VALUE,
        order: 2,
        width: 40,
        type: TableCellType.WRAPPED_STRING,
      },
      {
        label: "Current Value",
        key: QuickReviewDiffTableColumnKeys.NEW_VALUE,
        order: 3,
        width: 40,
        type: TableCellType.WRAPPED_STRING,
      },
    ];
  }

  /**
   * Transforms the board detail's field comparisons into rows for the
   * field-diff table: the monitored field name, the value the order was
   * last worked in, and the newly ingested value.
   *
   * @param fields - Every monitored field comparison from the detail response
   * @returns Table row data keyed by QuickReviewDiffTableColumnKeys
   */
  static generateTableDataFromFieldComparisons(
    fields: QuickReviewFieldComparison[],
  ): QuickReviewDiffTableData[] {
    return fields
      .filter((field) => field.changed)
      .map((field) => ({
        [QuickReviewDiffTableColumnKeys.PARAMETER]:
          getFieldDisplayLabel(field.field),
        [QuickReviewDiffTableColumnKeys.OLD_VALUE]: formatFieldValue(
          field.worked_value,
        ),
        [QuickReviewDiffTableColumnKeys.NEW_VALUE]: formatFieldValue(
          field.new_value,
        ),
        [QuickReviewDiffTableColumnKeys.IS_CHANGED]: field.changed,
      }));
  }
}

/**
 * Resolves the display label for a monitored field name. Known snake_case
 * keys and legacy preview labels are remapped; everything else falls back
 * to the capitalized raw name.
 *
 * @param fieldName - Monitored field name from the detail response
 * @returns Human-readable label for the Parameter column
 */
const getFieldDisplayLabel = (fieldName: string): string => {
  const mappedLabel = QUICK_REVIEW_FIELD_LABELS[fieldName];
  if (mappedLabel !== undefined) {
    return mappedLabel;
  }
  return capitalizeWordsSeperatedByUnderScore(fieldName);
};

/**
 * Renders a monitored field value as displayable text for the diff table.
 *
 * Missing values become empty strings, plain values are stringified, and
 * structured values (e.g. date objects) are serialized as JSON.
 *
 * @param value - Raw worked or newly ingested value from the detail response
 * @returns Displayable string for the table cell
 */
const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
